/**
 * Textos da retomada de contato.
 *
 * Três decisões de tom que valem explicar, porque não são óbvias:
 *
 * 1. **Ninguém finge que lembra.** A mensagem diz de onde veio o contato — a
 *    visita, com data. Fingir intimidade com quem não fechou soa a telemarketing.
 * 2. **O motivo é reconhecido, não contornado.** Quem saiu por valor recebe uma
 *    frase sobre condições; quem mudou de cidade não recebe mensagem nenhuma.
 * 3. **Sem promessa que a escola não fez.** Nada de "temos uma oferta especial"
 *    ou "vaga garantida" — quem escreve isso é o consultor, se for verdade.
 *
 * Tudo aqui é rascunho: o texto abre no WhatsApp e a pessoa edita antes de
 * enviar. É por isso que ele pode ser específico sem ser arriscado.
 */
import { paraE164 } from "./paises";

/** Motivos em que insistir é falta de educação, não persistência. */
export const MOTIVOS_SEM_RETOMADA = ["MUDANCA_CIDADE", "DESISTIU_MUDAR"];

const primeiroNome = (nome: string) => nome.trim().split(/\s+/)[0] ?? nome;

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const mesDe = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

/**
 * A abertura muda conforme o que aconteceu de verdade.
 *
 * Quem não compareceu **não visitou** a escola — dizer que visitou é o tipo de
 * erro que a família percebe na primeira linha e que destrói a conversa antes
 * de ela começar.
 */
function abertura(status: string, aluno: string, quando: string): string {
  const visita = quando ? ` em ${quando}` : "";
  if (status === "NAO_COMPARECEU") {
    return `Você agendou uma visita à escola${visita} para conhecer nossa proposta para o(a) ${aluno}, e ela acabou não acontecendo.`;
  }
  return `Você visitou a escola${visita} para conhecer nossa proposta para o(a) ${aluno}.`;
}

/** Complemento conforme o que aconteceu — a frase do meio da mensagem. */
function complemento(status: string, motivo: string | null): string {
  if (status === "NAO_COMPARECEU") {
    return "Queria saber se você gostaria de remarcar — escolho um horário que caiba na sua rotina.";
  }
  if (status === "CANCELADO") {
    return "Queria saber se ainda faz sentido conversarmos sobre a escola para o ano que vem.";
  }
  // OPTOU_OUTRA_ESCOLA — aqui o motivo registrado muda a conversa
  switch (motivo) {
    case "VALOR":
      return "Estamos com as condições de 2027 definidas e queria muito apresentar os números atualizados para você, sem compromisso.";
    case "FALTA_VAGA":
      return "Abrimos as turmas de 2027 e agora temos vaga. Queria saber se ainda há interesse.";
    case "INFRAESTRUTURA":
    case "METODOLOGIA":
      return "Queria te contar o que mudou na escola desde a sua visita e saber se ainda faz sentido conversarmos.";
    case "ATENDIMENTO":
      return "Queria entender melhor como foi a sua experiência com a gente — o seu retorno ajuda mais do que você imagina.";
    default:
      return "Queria saber se ainda faz sentido conversarmos sobre a escola para o ano que vem.";
  }
}

export interface DadosRetomada {
  nomeResponsavel: string;
  nomeAluno: string;
  inicioEm: string;
  status: string;
  motivoNaoMatricula: string | null;
}

/** Rascunho da mensagem. Sempre editável antes de sair. */
export function mensagemRetomada(v: DadosRetomada): string {
  const abre = abertura(v.status, primeiroNome(v.nomeAluno), mesDe(v.inicioEm));
  return [
    `Olá, ${primeiroNome(v.nomeResponsavel)}! Aqui é da equipe do Colégio COCSJC — Unidade Sul.`,
    "",
    `${abre} ${complemento(v.status, v.motivoNaoMatricula)}`,
    "",
    "Se preferir, me responda por aqui mesmo. Fico à disposição!",
  ].join("\n");
}

/**
 * Acompanhamento de quem está no meio da negociação.
 *
 * Diferente da retomada: aqui a família **não** disse não, apenas parou de
 * responder. O tom é de disponibilidade, não de reconquista — e não se cobra
 * resposta, porque cobrar é o jeito mais rápido de virar mensagem ignorada.
 */
export function mensagemAcompanhamento(v: {
  nomeResponsavel: string;
  nomeAluno: string;
  status: string;
}): string {
  const quem = primeiroNome(v.nomeResponsavel);
  const aluno = primeiroNome(v.nomeAluno);

  const meio =
    v.status === "EM_PESQUISA"
      ? `Queria saber se você conseguiu ver a pesquisa que enviamos e se ficou alguma dúvida sobre a matrícula do(a) ${aluno}.`
      : `Estou à disposição para qualquer dúvida sobre a proposta que conversamos para o(a) ${aluno} — valores, material, horários, o que for.`;

  return [
    `Olá, ${quem}! Aqui é da equipe do Colégio COCSJC — Unidade Sul.`,
    "",
    meio,
    "",
    "Se preferir, me responda por aqui mesmo quando puder.",
  ].join("\n");
}

/**
 * Link que abre a conversa com o texto pronto.
 *
 * `wa.me` abre o WhatsApp instalado no computador quando existe, e cai na
 * versão web quando não existe — por isso ele, e não `whatsapp://`, que falha
 * em silêncio em máquina sem o aplicativo.
 */
/**
 * Texto que acompanha o PDF da proposta.
 *
 * Curto de propósito: o anexo é o conteúdo, a mensagem é só o aviso de que ele
 * chegou. Não repete valores — número solto no corpo do WhatsApp vira print
 * fora de contexto, e o que a família precisa comparar está no arquivo, com as
 * condições e a validade ao lado.
 */
export function mensagemOrcamento(
  nomeResponsavel: string,
  nomeAluno: string,
  serie?: string | null,
): string {
  const nome = primeiroNome(nomeResponsavel);
  const aluno = primeiroNome(nomeAluno);
  const curso = serie ? ` no ${serie}` : "";
  return [
    `Olá, ${nome}! Aqui é da equipe do Colégio COCSJC — Unidade Sul.`,
    "",
    `Preparei a proposta de investimento para o(a) ${aluno}${curso}, com os valores, as condições e o material didático. Vou anexar o arquivo aqui embaixo.`,
    "",
    "Qualquer dúvida sobre os valores é só me chamar por aqui. E se quiser conhecer a escola pessoalmente, me avise o melhor dia e horário que eu reservo para vocês.",
  ].join("\n");
}

/**
 * Link que abre a conversa no WhatsApp com o texto pronto.
 *
 * O número vai em E.164 — código do país junto —, por isso o `pais` precisa
 * vir. Sem ele, o antigo grudava "55" em qualquer número de 10 ou 11 dígitos, e
 * um celular americano abria a conversa de um brasileiro qualquer.
 */
export function linkWhatsapp(
  telefone: string | null,
  texto: string,
  pais?: string | null,
): string | null {
  const numero = paraE164(telefone, pais);
  if (!numero) return null;
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}
